import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const functions = getFunctions();
const db = getFirestore();

/**
 * Get a profile by ID
 * @param {string} profileId - The profile ID
 * @returns {Promise<Object>} Profile data with isOwner flag
 */
export async function getProfile(profileId) {
  try {
    const getProfileFn = httpsCallable(functions, 'getProfile');
    const result = await getProfileFn({ profileId });
    return result.data;
  } catch (error) {
    console.error('Error getting profile:', error);
    throw error;
  }
}

/**
 * Get profile for a user (by userId)
 * @param {string} userId - The user's ID
 * @returns {Promise<Object|null>} Profile data or null if not found
 */
export async function getProfileByUserId(userId) {
  try {
    // Profile ID format is {userId}_career
    const profileId = `${userId}_career`;
    const result = await getProfile(profileId);
    return result;
  } catch (error) {
    if (error.code === 'functions/not-found') {
      return null;
    }
    throw error;
  }
}

/**
 * Update profile data
 * @param {string} profileId - The profile ID
 * @param {Object} updates - Fields to update (currentRole, targetRole, yearsExperience, skills)
 * @returns {Promise<Object>} Update result
 */
export async function updateProfile(profileId, updates) {
  try {
    const updateProfileFn = httpsCallable(functions, 'updateProfile');
    const result = await updateProfileFn({ profileId, updates });
    return result.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
}

/**
 * Update a training plan item status
 * @param {string} profileId - The profile ID
 * @param {string} itemId - The training item ID
 * @param {string} status - 'not_started' | 'in_progress' | 'completed'
 * @param {string} [notes] - Optional notes
 * @returns {Promise<Object>} Update result with new summary
 */
export async function updateTrainingPlanItem(profileId, itemId, status, notes) {
  try {
    const updateItemFn = httpsCallable(functions, 'updateTrainingPlanItem');
    const result = await updateItemFn({ profileId, itemId, status, notes });
    return result.data;
  } catch (error) {
    console.error('Error updating training item:', error);
    throw error;
  }
}

/**
 * Check if a profile exists for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} True if profile exists
 */
export async function profileExists(userId) {
  try {
    const profileId = `${userId}_career`;
    const profileRef = doc(db, 'profiles', profileId);
    const profileDoc = await getDoc(profileRef);
    return profileDoc.exists();
  } catch (error) {
    console.error('Error checking profile existence:', error);
    return false;
  }
}

/**
 * Get the profile ID for a user
 * @param {string} userId - The user's ID
 * @returns {string} Profile ID
 */
export function getProfileIdForUser(userId) {
  return `${userId}_career`;
}

/**
 * Add a qualified role milestone to the profile
 * @param {string} profileId - The profile ID
 * @param {Object} milestone - The milestone data
 * @returns {Promise<Object>} Update result
 */
export async function addQualifiedRole(profileId, milestone) {
  try {
    const addQualifiedRoleFn = httpsCallable(functions, 'addQualifiedRole');
    const result = await addQualifiedRoleFn({ profileId, milestone });
    return result.data;
  } catch (error) {
    console.error('Error adding qualified role:', error);
    throw error;
  }
}

/**
 * Update user's phone number (stored in privateData)
 * @param {string} profileId - The profile ID
 * @param {string} phone - The phone number
 * @returns {Promise<Object>} Update result
 */
export async function updatePhone(profileId, phone) {
  try {
    // Phone is stored in privateData for privacy
    const updateProfileFn = httpsCallable(functions, 'updateProfile');
    const result = await updateProfileFn({ 
      profileId, 
      updates: { 
        privateData: { phone },
        hasPhone: !!phone // Flag for quick checks
      } 
    });
    return result.data;
  } catch (error) {
    console.error('Error updating phone:', error);
    throw error;
  }
}

